<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content d-flex justify-content-between align-items-start">
                          <div>
                            <h4 class="nk-block-title">{{ $t('dm_dashboard_title') }}</h4>
                            <p>{{ $t('dm_dashboard_subtitle') }}</p>
                          </div>
                          <button v-if="canDmAdmin" class="btn btn-sm btn-outline-danger" @click="resetAllData" :disabled="resetting">
                            <em class="icon ni ni-reload"></em> {{ resetting ? $t('dm_dashboard_btn_reset_deleting') : $t('dm_dashboard_btn_reset') }}
                          </button>
                        </div>
                      </div>

                      <!-- Kennzahlen -->
                      <div class="d-flex flex-wrap mb-4" style="gap: 10px;">
                        <div class="kz-chip" v-for="kz in kennzahlen" :key="kz.key" @click="navigateTo(kz.route)">
                          <div class="kz-icon-wrap">
                            <em class="icon ni" :class="kz.icon"></em>
                          </div>
                          <span class="kz-label">{{ kz.label }}</span>
                          <span class="kz-value">{{ getCount(kz.key) }}</span>
                          <span v-if="kz.key === 'anlagen' && overview.counts" class="kz-sub">
                            {{ overview.counts.anlagen_wea || 0 }} WEA &middot; {{ overview.counts.anlagen_pv || 0 }} PV &middot; {{ overview.counts.anlagen_bat || 0 }} BAT
                          </span>
                        </div>
                      </div>

                      <!-- Prüfungsfristen Ampel -->
                      <div class="card card-bordered mb-4" style="margin-top: 1.5rem;">
                        <div class="card-inner">
                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="overline-title overline-title-alt mb-0">{{ $t('dm_dashboard_section_inspection_dates') }}</h6>
                            <select class="form-control form-control-sm dm-select" v-model="filterAnlageTyp" @change="loadAmpel">
                              <option value="">{{ $t('dm_common_all_types') }}</option>
                              <option value="WEA">WEA</option>
                              <option value="PV">PV</option>
                              <option value="BAT">BAT</option>
                            </select>
                          </div>

                          <div v-if="hasAmpelData">
                            <div class="row g-gs">
                              <div class="col" v-for="cat in ampelCategories" :key="cat.key">
                                <div class="ampel-card text-center p-3" :style="{ borderLeft: '4px solid ' + cat.color, cursor: 'pointer' }" @click="navigateToCategory(cat.key)">
                                  <div class="ampel-count" :style="{ color: cat.color }">{{ getAmpelCount(cat.key) }}</div>
                                  <div class="text-muted small">{{ cat.label }}</div>
                                </div>
                              </div>
                            </div>

                            <div class="mt-3" v-if="ampelItems.length > 0">
                              <h6 class="overline-title overline-title-alt mb-2">{{ $t('dm_dashboard_next_due_inspections') }}</h6>
                              <div class="table-responsive">
                                <table class="table table-sm table-striped">
                                  <thead>
                                    <tr>
                                      <th style="width:50px;">{{ $t('dm_dashboard_col_ampel') }}</th>
                                      <th>{{ $t('dm_common_col_serial_number') }}</th>
                                      <th>{{ $t('dm_common_col_inspection') }}</th>
                                      <th>{{ $t('dm_common_col_due_date') }}</th>
                                      <th>{{ $t('dm_common_col_park') }}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="item in ampelItems" :key="item._id" class="table-row" @click="goToAnlage(item)">
                                      <td>
                                        <span class="badge" :style="{ backgroundColor: getAmpelColor(item.ampel) }">&nbsp;&nbsp;&nbsp;</span>
                                      </td>
                                      <td>{{ item.seriennummer }}</td>
                                      <td>{{ item.pruefung_name || item.bezeichnung }}</td>
                                      <td>{{ item.faellig_am | moment("DD.MM.YYYY") }}</td>
                                      <td>{{ item.park_name }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>

                          <div v-else class="text-center py-4">
                            <em class="icon ni ni-calendar-check" style="font-size: 2.5rem; color: #ccc;"></em>
                            <p class="text-muted mt-2" v-html="$t('dm_dashboard_no_inspection_dates')"></p>
                          </div>
                        </div>
                      </div>

                      <!-- Letzte Aktivitäten -->
                      <div class="card card-bordered">
                        <div class="card-inner">
                          <h6 class="overline-title overline-title-alt mb-3">{{ $t('dm_dashboard_section_recent_activity') }}</h6>
                          <div v-if="recentActivity.length > 0">
                            <div v-for="(act, idx) in recentActivity" :key="idx" class="d-flex align-items-start py-2" :class="{ 'border-top': idx > 0 }">
                              <em class="icon ni mr-3 mt-1" :class="getActivityIcon(act.action)" style="font-size: 1.2rem; color: #6576ff;"></em>
                              <div>
                                <span>{{ act.beschreibung || act.description }}</span>
                                <div class="text-muted small">{{ act.datum | moment("DD.MM.YYYY HH:mm") }} <span v-if="act.benutzer">— {{ act.benutzer }}</span></div>
                              </div>
                            </div>
                          </div>
                          <div v-else class="text-center py-4">
                            <em class="icon ni ni-activity-round" style="font-size: 2.5rem; color: #ccc;"></em>
                            <p class="text-muted mt-2">{{ $t('dm_dashboard_no_recent_activity') }}</p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getDashboardOverview, getPruefungsfristenDashboard, resetAll } from "@/services/dm.service";

export default {
  name: "DmDashboard",
  components: { Header, Navigation, Footer },
  mixins: [dmPermissionsMixin],
  data() {
    return {
      fullLoader: null,
      resetting: false,
      overview: {},
      recentActivity: [],
      ampelData: {},
      ampelItems: [],
      filterAnlageTyp: "",
    };
  },
  computed: {
    kennzahlen() {
      return [
        { key: "anlagen", label: this.$t('dm_common_anlagen'), icon: "ni-setting", route: "/dm/anlagen" },
        { key: "parks", label: this.$t('dm_common_parks'), icon: "ni-map-pin", route: "/dm/parks" },
        { key: "adressen", label: this.$t('dm_common_adressen'), icon: "ni-users", route: "/dm/adressen" },
        { key: "dokumente", label: this.$t('dm_common_dokumente'), icon: "ni-file-docs", route: "/dm/dokumente" },
      ];
    },
    ampelCategories() {
      return [
        { key: "ueberfaellig", label: this.$t('dm_dashboard_ampel_overdue'), color: "#DC2626" },
        { key: "rot", label: this.$t('dm_dashboard_ampel_90days'), color: "#EF4444" },
        { key: "orange", label: this.$t('dm_dashboard_ampel_180days'), color: "#F97316" },
        { key: "gelb", label: this.$t('dm_dashboard_ampel_270days'), color: "#EAB308" },
        { key: "gruen", label: this.$t('dm_dashboard_ampel_270plus'), color: "#24c45f" },
      ];
    },
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page == "setup";
    },
    hasAmpelData() {
      if (!this.ampelData) return false;
      return Object.keys(this.ampelData).some((k) => {
        var val = this.ampelData[k];
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === "number") return val > 0;
        return false;
      });
    },
  },
  created() {
    this.loadOverview();
    this.loadAmpel();
  },
  methods: {
    loadOverview() {
      this.showLoader();
      getDashboardOverview()
        .then((d) => {
          this.hideLoader();
          if (d.data) {
            this.overview = d.data;
            this.recentActivity = d.data.recent_activity || [];
          }
        })
        .catch((e) => {
          this.hideLoader();
          this.$store.dispatch("api_error", e);
        });
    },
    loadAmpel() {
      var filters = {};
      if (this.filterAnlageTyp) filters.anlage_typ = this.filterAnlageTyp;
      getPruefungsfristenDashboard(filters)
        .then((d) => {
          if (d.data) {
            this.ampelData = d.data.summary || {};
            this.ampelItems = (d.data.items || []).slice(0, 10);
            this.$store.commit("set_dm_dashboard", d.data);
          }
        })
        .catch(() => {});
    },
    getCount(key) {
      if (!this.overview.counts) return "–";
      return this.overview.counts[key] != null ? this.overview.counts[key] : "–";
    },
    getAmpelCount(key) {
      var val = this.ampelData[key];
      if (Array.isArray(val)) return val.length;
      if (typeof val === "number") return val;
      return 0;
    },
    getAmpelColor(ampel) {
      var colors = {
        ueberfaellig: "#DC2626",
        rot: "#EF4444",
        orange: "#F97316",
        gelb: "#EAB308",
        gruen: "#24c45f",
      };
      return colors[ampel] || "#999";
    },
    getActivityIcon(action) {
      var icons = {
        created: "ni-plus-circle",
        updated: "ni-edit",
        deleted: "ni-trash",
        confirmed: "ni-check-circle",
        uploaded: "ni-upload",
      };
      return icons[action] || "ni-activity-round";
    },
    navigateTo(route) {
      this.$router.push(route);
    },
    navigateToCategory(key) {
      this.$router.push({ path: "/dm/anlagen", query: { ampel: key } });
    },
    goToAnlage(item) {
      if (item.anlage_id) {
        this.$router.push("/dm/anlage/" + item.anlage_id);
      }
    },
    resetAllData() {
      this.$swal({
        title: this.$t('dm_dashboard_confirm_reset_title'),
        html: this.$t('dm_dashboard_confirm_reset_text'),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: this.$t('dm_dashboard_btn_confirm_delete_all'),
        cancelButtonText: this.$t('dm_common_btn_cancel'),
      }).then((result) => {
        if (result.isConfirmed || result.value) {
          this.resetting = true;
          resetAll()
            .then((d) => {
              this.resetting = false;
              if (d && d.status === "error") {
                this.$swal(this.$t('dm_common_error'), d.message || this.$t('dm_common_error_unknown'), "error");
                return;
              }
              var summary = d.data || {};
              var total = Object.values(summary).reduce(function(a, b) { return a + (b || 0); }, 0);
              this.$swal(this.$t('dm_dashboard_success_reset'), total + " " + this.$t('dm_dashboard_success_reset_count'), "success");
              this.loadOverview();
              this.loadAmpel();
            })
            .catch((e) => {
              this.resetting = false;
              this.$store.dispatch("api_error", e);
            });
        }
      });
    },
    showLoader() {
      this.fullLoader = this.$loading.show({
        container: null, canCancel: false, height: 40, width: 40,
        loader: "spinner", color: "#333333", backgroundColor: "#EEF2FE", opacity: 0.6,
      });
    },
    hideLoader() {
      if (this.fullLoader) this.fullLoader.hide();
    },
  },
};
</script>

<style scoped>
.kz-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 16px; border-radius: 8px;
  border: 1px solid #e2e8f0; background: #fff;
  cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s;
}
.kz-chip:hover { border-color: #6576ff; box-shadow: 0 1px 6px rgba(0,0,0,0.06); }
.kz-icon-wrap {
  width: 30px; height: 30px; border-radius: 6px;
  background: #f0f2ff; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.kz-icon-wrap .icon { font-size: 1rem; color: #6576ff; }
.kz-label { font-size: 0.8rem; color: #8094ae; font-weight: 500; text-transform: uppercase; letter-spacing: 0.03em; }
.kz-value { font-size: 1.1rem; font-weight: 700; color: #364a63; }
.kz-sub { font-size: 0.75rem; color: #8094ae; }
.dm-select { width: 160px !important; color: #364a63 !important; font-size: 0.85rem; }
.ampel-card { background: #fafafa; border-radius: 6px; transition: background 0.15s; }
.ampel-card:hover { background: #f0f0f0; }
.ampel-count { font-size: 1.8rem; font-weight: 700; line-height: 1.2; }
.table-row { cursor: pointer; }
.table-row:hover { background-color: #f5f5f5; }
</style>
